import { Calendar, Clock, MapPin, Phone, CheckCircle, Shield, Zap, Star } from "lucide-react";
import { AppointmentForm } from "../components/AppointmentForm";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";

const benefits = [
  {
    icon: CheckCircle,
    title: "Diagnostic gratuit",
    description: "Évaluation complète de votre appareil sans engagement"
  },
  {
    icon: Clock,
    title: "Réparation rapide",
    description: "La plupart des réparations effectuées en moins d'1 heure"
  },
  {
    icon: Shield,
    title: "Garantie incluse",
    description: "Toutes nos réparations sont garanties 6 à 12 mois"
  },
  {
    icon: Star,
    title: "Experts certifiés",
    description: "Techniciens qualifiés et spécialistes Apple"
  }
];

const openingHours = [
  { day: "Lundi - Vendredi", hours: "10h00 - 18h00" },
  { day: "Samedi", hours: "10h00 - 16h00" },
  { day: "Dimanche", hours: "Fermé" }
];

export default function AppointmentPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="appointment-page">
      <SEO
        title="Prendre Rendez-vous Réparation à Niort - Mediastore"
        description="Prenez rendez-vous en ligne avec Mediastore Niort. Réparation iPhone, iPad, Mac, MacBook, ordinateur. Techniciens certifiés Apple. Diagnostic gratuit sur place. Créneaux du lundi au samedi."
        keywords="prendre rendez-vous réparation Niort, RDV réparateur Niort, rendez-vous Apple Niort, rendez-vous iPhone Niort, rendez-vous Mac Niort, prise de rendez-vous Mediastore"
        path="/rendez-vous"
        breadcrumbs={[
          { name: "Accueil", path: "/" },
          { name: "Rendez-vous", path: "/rendez-vous" },
        ]}
      />
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-16 md:py-20">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-red-600/20 text-red-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Calendar className="h-4 w-4" />
              Prise de rendez-vous en ligne
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
              Prenez rendez-vous pour votre{" "}
              <span className="text-red-500">réparation</span>
            </h1>
            <p className="text-lg text-slate-300 mb-8">
              Réservez un créneau qui vous convient et bénéficiez d&apos;un diagnostic gratuit. 
              Notre équipe vous recontactera pour confirmer votre rendez-vous.
            </p>
            
            {/* Quick Info */}
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                <MapPin className="h-4 w-4 text-blue-400" />
                <span>104 Rue de la Gare, Niort</span>
              </div>
              <a 
                href="tel:+33549057490" 
                className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full hover:bg-white/20 transition-colors"
              >
                <Phone className="h-4 w-4 text-green-400" />
                <span>05 49 05 74 90</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 md:py-16">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
            {/* Form Section */}
            <div className="lg:col-span-2">
              <AppointmentForm />
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Benefits Card */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="font-bold text-lg text-slate-900 mb-4">
                  Pourquoi prendre rendez-vous ?
                </h3>
                <div className="space-y-4">
                  {benefits.map((benefit) => {
                    const Icon = benefit.icon;
                    return (
                      <div key={benefit.title} className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                          <Icon className="h-5 w-5 text-red-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-900 text-sm">{benefit.title}</h4>
                          <p className="text-slate-600 text-sm">{benefit.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Opening Hours Card */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="font-bold text-lg text-slate-900 mb-4 flex items-center gap-2">
                  <Clock className="h-5 w-5 text-red-600" />
                  Horaires d&apos;ouverture
                </h3>
                <div className="space-y-3">
                  {openingHours.map((item) => (
                    <div 
                      key={item.day} 
                      className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0"
                    >
                      <span className="text-slate-600">{item.day}</span>
                      <span className={`font-medium ${item.hours === "Fermé" ? "text-red-500" : "text-slate-900"}`}>
                        {item.hours}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Emergency CTA */}
              <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-2xl p-6 text-white">
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="h-5 w-5" />
                  <h3 className="font-bold text-lg">Urgence ?</h3>
                </div>
                <p className="text-red-100 text-sm mb-4">
                  Besoin d&apos;une réparation immédiate ? Appelez-nous directement ou passez en boutique.
                </p>
                <a href="tel:+33549057490">
                  <Button 
                    variant="secondary" 
                    className="w-full bg-white text-red-600 hover:bg-red-50 gap-2"
                    data-testid="emergency-call-btn"
                  >
                    <Phone className="h-4 w-4" />
                    Appeler maintenant
                  </Button>
                </a>
              </div>

              {/* Location Card */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="font-bold text-lg text-slate-900 mb-4 flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-red-600" />
                  Notre adresse
                </h3>
                <p className="text-slate-600 mb-4">
                  104 Rue de la Gare<br />
                  79000 Niort, France
                </p>
                <a
                  href="https://www.google.com/maps/place/104+Rue+de+la+Gare,+79000+Niort,+France"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" className="w-full gap-2">
                    <MapPin className="h-4 w-4" />
                    Voir sur Google Maps
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
